import { t } from "../i18n/runtime";
import { io } from "socket.io-client";
import type { Socket } from "socket.io-client";
import type { GameEvent, NationAgentView, NationId } from "@cwb/core";
import { SOCKET_EVENTS } from "@cwb/protocol";
import type {
  AgentDecisionPayload,
  AgentDoctrinePayload,
  AgentStatusPayload,
  ClientToServerEvents,
  EventsPayload,
  ServerToClientEvents,
  SessionEndedPayload,
  WorldPatch,
  WorldSnapshot,
} from "@cwb/protocol";
import { useGameStore } from "../stores/gameStore";
import { gameBridge } from "../game/GameBridge";
import { createPlayerForwarder } from "../portal/playerForwarder";
import { getPortalBridge } from "../portal/portalBridge";
import { BANNER_TITLE, formatEndReason, translateError } from "../i18n";

/**
 * Realtime channel. Reads only — every player action goes over HTTP so the
 * server keeps a single validated write path (§20.2).
 */

type GameSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

let socket: GameSocket | null = null;

function nationName(nationId: NationId): string {
  const map = useGameStore.getState().map;
  return (
    map?.nations.find((n) => n.id === nationId)?.name ?? nationId.toUpperCase()
  );
}

/**
 * Promote the loudest beat in a batch to the slam-in banner. One per batch so a
 * busy tick cannot machine-gun the player.
 */
function cueEvents(events: GameEvent[]): void {
  const store = useGameStore.getState();

  // A betrayal outranks a plain declaration in the same batch: the two arrive
  // together (the pact is torn up and the war declared in one turn) and the
  // interesting half is the treachery, not the war.
  const betrayal = events.find((e) => e.type === "WAR_DECLARED" && e.betrayal);
  if (betrayal && betrayal.type === "WAR_DECLARED") {
    store.showBanner(
      BANNER_TITLE.betrayal,
      t("socket.broke_its_alliance_and_attacked", {
        p0: nationName(betrayal.nationId),
        p1: nationName(betrayal.targetNationId),
      }),
      "war",
    );
    return;
  }

  const war = events.find((e) => e.type === "WAR_DECLARED");
  if (war && war.type === "WAR_DECLARED") {
    store.showBanner(
      BANNER_TITLE.warDeclared,
      t("socket.attacked", {
        p0: nationName(war.nationId),
        p1: nationName(war.targetNationId),
      }),
      "war",
    );
    return;
  }

  const alliance = events.find((e) => e.type === "ALLIANCE_FORMED");
  if (alliance && alliance.type === "ALLIANCE_FORMED") {
    store.showBanner(
      BANNER_TITLE.allianceFormed,
      `${nationName(alliance.nationId)} · ${nationName(alliance.targetNationId)}`,
      "ai",
    );
    return;
  }

  const eliminated = events.find((e) => e.type === "NATION_ELIMINATED");
  if (eliminated && eliminated.type === "NATION_ELIMINATED") {
    store.showBanner(
      BANNER_TITLE.nationEliminated,
      nationName(eliminated.nationId),
      "war",
    );
    return;
  }

  const peace = events.find((e) => e.type === "PEACE_AGREED");
  if (peace && peace.type === "PEACE_AGREED") {
    store.showBanner(
      BANNER_TITLE.peaceAgreed,
      `${nationName(peace.nationId)} · ${nationName(peace.targetNationId)}`,
      "trade",
    );
    return;
  }

  const captured = events.find((e) => e.type === "PROVINCE_CAPTURED");
  if (captured && captured.type === "PROVINCE_CAPTURED") {
    const province =
      store.map?.provinces.find((p) => p.id === captured.provinceId)?.name ??
      captured.provinceId;
    store.showBanner(
      BANNER_TITLE.provinceCaptured,
      t("socket.captured", {
        p0: nationName(captured.toNationId),
        p1: province,
      }),
      "war",
    );
  }
}

function blankAgent(nationId: NationId): NationAgentView {
  return {
    nationId,
    status: "IDLE",
    sourceMode: "mock",
    kingSourceMode: null,
    turnCount: 0,
    currentGoal: "",
    lastDecisionSummary: "",
    lastPublicStatement: "",
    doctrineLines: [],
    doctrineUpdatedTurn: null,
    lastDoctrinePatch: null,
    memoryLines: [],
    threadId: null,
    lastTurnAtTick: null,
    lastTrigger: null,
    failureReason: null,
  };
}

/**
 * Fold a narrow agent payload into the stored view. Decision and doctrine
 * events can outrun the first snapshot, so a missing nation is seeded rather
 * than dropped.
 */
function patchAgent(nationId: NationId, patch: Partial<NationAgentView>): void {
  const store = useGameStore.getState();
  const existing =
    store.agents.find((a) => a.nationId === nationId) ?? blankAgent(nationId);
  store.upsertAgent({ ...existing, ...patch });
}

export function connectSession(sessionId: string): () => void {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }

  const next: GameSocket = io({ transports: ["websocket", "polling"] });
  socket = next;
  // Who the portal says is playing, if the game is embedded. Inert standalone.
  const player = createPlayerForwarder(
    getPortalBridge(),
    sessionId,
    (payload) => next.emit(SOCKET_EVENTS.player, payload),
    () => next.connected,
  );

  // "connect" fires on the first connection and on every reconnection, so
  // re-joining the room needs no separate reconnect handler.
  next.on("connect", () => {
    const current = useGameStore.getState();
    current.setConnected(true);
    current.setBootError(null);
    next.emit(SOCKET_EVENTS.join, { sessionId });
    // After the join: the server only takes an identity from a joined socket.
    player.sync();
  });

  next.on("disconnect", () => {
    useGameStore.getState().setConnected(false);
  });

  next.on("connect_error", () => {
    const current = useGameStore.getState();
    current.setConnected(false);
    // Rendered straight into the boot screen, so it is written in Korean here.
    if (!current.state)
      current.setBootError(
        t("socket.could_not_connect_to_the_operations_server"),
      );
  });

  next.on(SOCKET_EVENTS.snapshot, (payload: WorldSnapshot) => {
    useGameStore.getState().applySnapshot(payload);
  });

  next.on(SOCKET_EVENTS.patch, (payload: WorldPatch) => {
    useGameStore.getState().applyPatch(payload);
  });

  next.on(SOCKET_EVENTS.events, (payload: EventsPayload) => {
    useGameStore.getState().pushEvents(payload.events);
    cueEvents(payload.events);
  });

  next.on(SOCKET_EVENTS.agentStatus, (payload: AgentStatusPayload) => {
    useGameStore.getState().upsertAgent(payload.agent);
  });

  next.on(SOCKET_EVENTS.agentDecision, (payload: AgentDecisionPayload) => {
    patchAgent(payload.nationId, {
      lastDecisionSummary: payload.summary,
      lastPublicStatement: payload.publicStatement,
      turnCount: payload.turnCount,
    });
    gameBridge.pulseNation(payload.nationId);
  });

  next.on(SOCKET_EVENTS.agentDoctrine, (payload: AgentDoctrinePayload) => {
    patchAgent(payload.nationId, {
      doctrineLines: payload.doctrine,
      doctrineUpdatedTurn: payload.turnCount,
      turnCount: payload.turnCount,
      lastDoctrinePatch: {
        reason: payload.reason,
        additions: payload.additions,
        removals: payload.removals,
      },
    });
    useGameStore
      .getState()
      .showToast(
        t("socket.doctrine_updated", { p0: nationName(payload.nationId) }),
        "info",
      );
  });

  next.on(SOCKET_EVENTS.sessionEnded, (payload: SessionEndedPayload) => {
    const current = useGameStore.getState();
    current.setResult(payload.result);
    current.showBanner(
      BANNER_TITLE.sessionEnded,
      formatEndReason(payload.result.reason),
      "ai",
    );
  });

  next.on(SOCKET_EVENTS.error, (payload: { error: string }) => {
    // The server refuses in English on purpose — Korean is put on at this edge.
    useGameStore.getState().showToast(translateError(payload.error), "error");
  });

  return () => {
    player.dispose();
    if (next.connected) next.emit(SOCKET_EVENTS.leave, { sessionId });
    next.removeAllListeners();
    next.disconnect();
    if (socket === next) socket = null;
    useGameStore.getState().setConnected(false);
  };
}
